import {
  buildPath,
  type ErrorBody,
  type ErrorCode,
  type RouteBody,
  type RouteDef,
  type RouteParams,
  type RoutePath,
  type RouteQuery,
  type RouteResponse,
} from '@vinyl/shared';

/** Reads an error answer without Zod, which stays out of the web bundle. */
function readErrorBody(value: unknown): ErrorBody | undefined {
  const v = value as ErrorBody | undefined;
  return v && typeof v === 'object' && v.error && typeof v.error.code === 'string' && typeof v.requestId === 'string' ? v : undefined;
}

/** An error answer from the API, carrying the shared error code. */
export class ApiError extends Error {
  override name = 'ApiError';
  readonly status: number;
  readonly code: ErrorCode;
  readonly fields: Record<string, ErrorCode> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ErrorBody | undefined) {
    super(body?.error.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body?.error.code ?? 'internal';
    this.fields = body?.error.fields;
    this.requestId = body?.requestId;
  }
}

type Listener = (response: Response) => void;
const listeners = new Set<Listener>();

/** Lets the app watch every API response (stale build check, 401 handling). */
export function onApiResponse(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

type CallArgs<D> = (RouteParams<RoutePath<D>> extends undefined ? { params?: undefined } : { params: RouteParams<RoutePath<D>> }) &
  (RouteBody<D> extends undefined ? { body?: undefined } : { body: RouteBody<D> }) &
  (RouteQuery<D> extends undefined ? { query?: undefined } : { query?: RouteQuery<D> });

/**
 * Calls a shared route. Request and response types come from the route's
 * Zod schemas in packages/shared.
 */
export async function api<D extends RouteDef>(
  def: D,
  ...[args]: CallArgs<D> extends { params?: undefined; body?: undefined; query?: undefined } ? [CallArgs<D>?] : [CallArgs<D>]
): Promise<RouteResponse<D>> {
  const a = (args ?? {}) as { params?: Record<string, string>; body?: unknown; query?: Record<string, unknown> };
  let url = buildPath(def.path, a.params);
  if (a.query) {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(a.query)) if (v !== undefined && v !== null) search.set(k, String(v));
    const qs = search.toString();
    if (qs) url += `?${qs}`;
  }
  const response = await fetch(url, {
    method: def.method,
    credentials: 'same-origin',
    headers: a.body !== undefined ? { 'content-type': 'application/json' } : {},
    ...(a.body !== undefined ? { body: JSON.stringify(a.body) } : {}),
  });
  for (const listener of listeners) listener(response);
  if (!response.ok) {
    throw new ApiError(response.status, readErrorBody(await response.json().catch(() => undefined)));
  }
  if (response.status === 204) return null as RouteResponse<D>;
  // The server validates every response against the route's schema.
  return (await response.json()) as RouteResponse<D>;
}
